/*
 * Image navigator - v1.0.0 - 2018-01-04
 * https://github.com/garbinluca/image-navigator
 * By Luca Garbin - lucagarbin.it
 * 
*/

import { TweenMax } from 'gsap';

class ImageNavigator {

    constructor(__container) {

        // binding functions

        this.resizeHandler = this.resizeHandler.bind(this);
        this.mouseMoveHandler = this.mouseMoveHandler.bind(this);
        this.touchStartHandler = this.touchStartHandler.bind(this);
        this.init = this.init.bind(this);

        // initializing

        this.$window = window;
        this.$container = __container;
        this.$image = this.$container.querySelector('img');

        // config object

        this.config = {
            direction: 'horizontal',
            offsetMax: 0,
            containerSize: {
                width: 0,
                height: 0,
                ratio: 0
            },
            originalImageSize: {
                width: 0,
                height: 0,
                ratio: 0,
                horizontal: false
            }
        }

        this.bindWindowEvents();
        this.getContainerSizes(); 

        this.loadImage().then(() => {
            this.init();
        })

    }

    getContainerSizes() {

        this.config.containerSize.width = this.$container.clientWidth;
        this.config.containerSize.height = this.$container.clientHeight;
        this.config.containerSize.ratio = this.config.containerSize.width / this.config.containerSize.height;

    }

    /*
     * Resize event handler
     * 
     */

    resizeHandler() {

        this.getContainerSizes();
        this.centerImage();

    }

    /*
     * Bind window events
     * 
     */

    bindWindowEvents() {

        this.$window.addEventListener('resize', this.resizeHandler);

    }

    /*
     * Unbind window events
     * 
     */

    unbindWindowEvents() {

        this.$window.removeEventListener('resize', this.resizeHandler);

    }

    /* 
     * Load image 
     * 
     */

    loadImage() {

        return new Promise((resolve, reject) => {

            if (this.$image.complete) {
                this.saveImageSizes();
                resolve();
            }

            this.$image.onload = () => {
                this.saveImageSizes();
                resolve();
            }

        });

    }

    saveImageSizes() {

        this.config.originalImageSize.width = this.$image.naturalWidth;
        this.config.originalImageSize.height = this.$image.naturalHeight;
        this.config.originalImageSize.ratio = this.config.originalImageSize.width / this.config.originalImageSize.height;
        this.config.originalImageSize.horizontal = this.$image.width > this.$image.height;
    }

    /*
     * Init function
     * 
     */

    init() {

        this.$image.style.position = 'absolute';
        this.$image.style.top = '0px';
        this.$image.style.left = '0px';

        this.centerImage();
        this.bindMoveEvents();

    }

    /*
     * Bind move events
     * 
     */

    bindMoveEvents() {

        this.$container.addEventListener('mousemove', this.mouseMoveHandler);
        this.$container.addEventListener('touchstart', this.touchStartHandler);
        this.$container.addEventListener('touchmove', this.mouseMoveHandler);

    }

    /*
     * Unbind move events 
     * 
     */

    unbindMoveEvents() {
        
        this.$container.removeEventListener('mousemove', this.mouseMoveHandler);
        this.$container.removeEventListener('touchstart', this.touchStartHandler);
        this.$container.removeEventListener('touchmove', this.mouseMoveHandler);

    }

    centerImage() {

        let newImageWidth, newImageHeight;

        if (this.config.originalImageSize.ratio < this.config.containerSize.ratio) {

            this.config.direction = 'vertical';
            newImageWidth = this.config.containerSize.width;
            newImageHeight = newImageWidth / this.config.originalImageSize.ratio;
            this.config.offsetMax = newImageHeight - this.config.containerSize.height;

        } else {

            this.config.direction = 'horizontal';
            newImageHeight = this.config.containerSize.height;
            newImageWidth = newImageHeight * this.config.originalImageSize.ratio;
            this.config.offsetMax = newImageWidth - this.config.containerSize.width;

        }

        this.$image.width = newImageWidth;
        this.$image.height = newImageHeight;

        if (
            (this.config.direction === 'horizontal' && this.config.originalImageSize.horizontal)
        ) {

            TweenMax.set(this.$image, {
                y: 0,
                x: (this.$image.width - this.config.containerSize.width) * -0.5
            })

        } else {

            TweenMax.set(this.$image, {
                y: 0,
                x: 0
            })

        }

    }

    /*
     * Mouse move event
     * 
     */

    mouseMoveHandler(e) {

        e.preventDefault();
        
        let move, factor = -1, rapporto, containerSize, moveObject, startFrom;
		const isVertical = this.config.direction !== 'horizontal';

        containerSize = isVertical ? this.config.containerSize.height : this.config.containerSize.width;

        if(e.type != 'touchmove'){

            move = isVertical ? e.layerY : e.layerX;
            rapporto = this.config.offsetMax * (move / containerSize * factor);

        }else if (e.touches && e.touches.length){

            const touch = e.touches[0];
            move = this.touchStartFrom - (isVertical ? touch.clientY : touch.clientX);

            rapporto = this.imageMovedOffset - (move * 1.5);

        }

        if(rapporto > 0){
            rapporto = 0;
        }

        if(Math.abs(rapporto) > Math.abs(this.config.offsetMax)){
			rapporto = Math.abs(this.config.offsetMax) * -1;
		}

        moveObject = {
            x: isVertical ? 0 : rapporto,
            y: isVertical ? rapporto : 0,
            overwrite: 'all',
            opacity: 1
        };

        TweenMax.to(this.$image, 0.3, moveObject)

    }

    /*
     * Touch start handler
     * 
     */

    touchStartHandler(e) {

        e.preventDefault();

        const touch = e.touches[0];
		const transform = this.$image._gsTransform;
        this.imageMovedOffset = this.config.direction !== 'horizontal' ? transform.y : transform.x;
        this.touchStartFrom = this.config.direction === 'horizontal' ? touch.clientX : touch.clientY;

	}

    destroy() {

        this.unbindMoveEvents();
        this.unbindWindowEvents();

    }

}

export default ImageNavigator;


// WEBPACK FOOTER //
// ./js/plugins/imageNavigator.js